import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { LayoutGrid, Building2, ClipboardCheck, Gauge, BookOpen, ListChecks, AlertTriangle, FileWarning, Clock, CircleDot } from "lucide-react";
import DoubleMaterialityChart, { PILLAR } from "../components/materiality/DoubleMaterialityChart";
import { OrganisationIssues, AssessmentEngagement, IssuePerformance, StandardsDisclosures, ActionsReview } from "../components/materiality/MaterialityViews";

const CLASS_TONE = {
  material_both: "text-accent border-accent/50",
  impact_material: "text-[#00E676] border-[#00E676]/40",
  financial_material: "text-[#4FC3F7] border-[#4FC3F7]/40",
  below_threshold: "text-muted-foreground border-border",
};

function Stat({ label, value, icon: Icon, tone, testid }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="card-flat p-4 hover:-translate-y-0.5 transition-transform" data-testid={testid}>
      <div className="label-eyebrow text-[10px] mb-1.5 flex items-center gap-1"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className={`font-display text-3xl font-bold leading-none ${tone || ""}`}>{value}</div>
    </motion.div>
  );
}

function Panel({ title, icon: Icon, items, empty, onSelect, renderItem, testid }) {
  return (
    <div className="card-flat p-4" data-testid={testid}>
      <div className="label-eyebrow text-[10px] mb-2 flex items-center gap-1"><Icon className="h-3.5 w-3.5" /> {title} <Badge variant="outline" className="ml-auto text-[9px]">{items.length}</Badge></div>
      {items.length === 0 ? <div className="text-[11px] text-muted-foreground py-2">{empty}</div> : (
        <div className="space-y-1.5">{items.map((x) => (
          <button key={x.id || x.code} onClick={() => onSelect && onSelect(x)} className="w-full text-left text-[11px] p-2 rounded-sm border border-border bg-surface hover:bg-surface-hover flex items-start gap-2" data-testid={`${testid}-item-${x.code}`}>
            {renderItem(x)}
          </button>
        ))}</div>
      )}
    </div>
  );
}

function Overview({ data, onOpenAssessment }) {
  const [pillarFilter, setPillarFilter] = useState("all");
  const [quadFilter, setQuadFilter] = useState("all");
  if (!data) return <div className="p-10 text-center text-muted-foreground font-mono text-sm">Loading overview…</div>;
  if (data.empty) return (
    <div className="card-flat p-12 text-center" data-testid="mat-overview-empty">
      <LayoutGrid className="h-8 w-8 text-accent mx-auto mb-3" />
      <h2 className="font-display text-2xl font-bold">Start your materiality assessment</h2>
      <p className="text-muted-foreground mt-2 max-w-md mx-auto">Set your organisation context and build a candidate issue register in the "Organisation & Issues" tab — issues will then plot on the double-materiality matrix here.</p>
    </div>
  );
  const s = data.stats;
  const p = data.panels;
  const QUADS = [["all", "All"], ["material_both", "Material — both"], ["impact_material", "Impact-material"], ["financial_material", "Financially material"], ["below_threshold", "Below threshold"]];

  return (
    <div className="space-y-5" data-testid="mat-view-overview">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <Stat label="Issues assessed" value={`${s.issues_assessed}/${s.issues_total}`} icon={ClipboardCheck} testid="mat-stat-assessed" />
        <Stat label="Material issues" value={s.material_issues} icon={CircleDot} tone="text-accent" testid="mat-stat-material" />
        <Stat label="Performance exceptions" value={s.performance_exceptions} icon={AlertTriangle} testid="mat-stat-exceptions" />
        <Stat label="Evidence gaps" value={s.evidence_gaps} icon={FileWarning} tone={s.evidence_gaps ? "text-[#FFB800]" : ""} testid="mat-stat-gaps" />
        <Stat label="Decisions awaiting" value={s.decisions_awaiting} icon={Clock} tone={s.decisions_awaiting ? "text-[#FFB800]" : ""} testid="mat-stat-decisions" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
        <div className="card-flat p-5">
          <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
            <div>
              <div className="label-eyebrow">Double-materiality matrix</div>
              <p className="text-[11px] text-muted-foreground mt-0.5">Impact (vertical) × financial (horizontal). Dots coloured by pillar; hollow = metric source missing; amber dot = evidence gap. The matrix is a reading aid — the approved decision is authoritative.</p>
            </div>
            <div className="flex items-center gap-3 text-[11px]">
              {Object.entries(PILLAR).map(([k, v]) => (
                <span key={k} className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: v.color }} /> {v.label}</span>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {Object.entries(PILLAR).concat([["all", { label: "All pillars" }]]).map(([k, v]) => (
              <button key={k} onClick={() => setPillarFilter(k)} className={`text-[10px] px-2 py-1 rounded-sm border ${pillarFilter === k ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:bg-surface-hover"}`} data-testid={`mat-filter-pillar-${k}`}>{v.label}</button>
            ))}
            <span className="w-px bg-border mx-1" />
            {QUADS.map(([k, lbl]) => (
              <button key={k} onClick={() => setQuadFilter(k)} className={`text-[10px] px-2 py-1 rounded-sm border ${quadFilter === k ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:bg-surface-hover"}`} data-testid={`mat-filter-quad-${k}`}>{lbl}</button>
            ))}
          </div>
          <DoubleMaterialityChart issues={data.issues} methodology={data.methodology} pillarFilter={pillarFilter} quadFilter={quadFilter} onSelect={onOpenAssessment} />
        </div>

        <div className="space-y-3">
          <Panel title="Decisions requiring attention" icon={Clock} items={p.decisions_awaiting} empty="No decisions awaiting approval." onSelect={onOpenAssessment} testid="mat-panel-decisions"
            renderItem={(x) => <><Clock className="h-3 w-3 text-[#FFB800] mt-0.5 shrink-0" /><span><span className="font-medium">{x.title}</span><br /><span className="text-muted-foreground">{x.reason}</span></span></>} />
          <Panel title="Evidence blind spots" icon={FileWarning} items={p.evidence_gaps} empty="No evidence gaps flagged." onSelect={onOpenAssessment} testid="mat-panel-gaps"
            renderItem={(x) => <><FileWarning className="h-3 w-3 text-[#FFB800] mt-0.5 shrink-0" /><span><span className="font-medium">{x.title}</span><br /><span className="text-muted-foreground">{x.reason}</span></span></>} />
          <Panel title="Assessment required" icon={AlertTriangle} items={p.assessment_required} empty="Every issue has been assessed." onSelect={onOpenAssessment} testid="mat-panel-assessment"
            renderItem={(x) => <><AlertTriangle className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" /><span><span className="font-medium">{x.title}</span><br /><span className="text-muted-foreground">Not yet assessed</span></span></>} />
          <Panel title="Performance exceptions" icon={AlertTriangle} items={p.performance_exceptions} empty="Performance tracking activates in Release 2." testid="mat-panel-exceptions" renderItem={(x) => <span>{x.title}</span>} />
        </div>
      </div>

      {data.issues.length > 0 && (
        <div className="card-flat p-5">
          <div className="label-eyebrow mb-3">Assessed issues</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {data.issues.map((i) => (
              <button key={i.id} onClick={() => onOpenAssessment(i)} className="text-left p-3 rounded-sm border border-border bg-surface hover:bg-surface-hover flex items-center gap-3" data-testid={`mat-issue-card-${i.code}`}>
                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: PILLAR[i.pillar]?.color }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{i.title}</div>
                  <div className="text-[10px] font-mono text-muted-foreground">I {i.impact_score} · F {i.financial_score}{i.missing_source ? " · source gap" : ""}</div>
                </div>
                <Badge variant="outline" className={`text-[8px] uppercase ${CLASS_TONE[i.classification]}`}>{data.class_label[i.classification]}</Badge>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Materiality() {
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);

  const loadOverview = async () => { const r = await api.get("/materiality/overview"); setOverview(r.data); };
  useEffect(() => { loadOverview(); }, []);

  const openAssessment = () => setTab("assessment");

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="materiality-page">
      <div>
        <div className="label-eyebrow mb-1">Module</div>
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-3"><LayoutGrid className="h-7 w-7 text-accent" /> Materiality Engine</h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-3xl">Double-materiality assessment — what matters, why, and (Release 2+) how well it is managed and what evidence supports it. Materiality, performance and evidence confidence are tracked as separate dimensions and never blended.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="mat-tab-overview"><LayoutGrid className="h-3.5 w-3.5 mr-1" /> Overview</TabsTrigger>
          <TabsTrigger value="organisation" data-testid="mat-tab-organisation"><Building2 className="h-3.5 w-3.5 mr-1" /> Organisation & Issues</TabsTrigger>
          <TabsTrigger value="assessment" data-testid="mat-tab-assessment"><ClipboardCheck className="h-3.5 w-3.5 mr-1" /> Assessment & Engagement</TabsTrigger>
          <TabsTrigger value="performance" data-testid="mat-tab-performance"><Gauge className="h-3.5 w-3.5 mr-1" /> Issue Performance</TabsTrigger>
          <TabsTrigger value="standards" data-testid="mat-tab-standards"><BookOpen className="h-3.5 w-3.5 mr-1" /> Standards & Disclosures</TabsTrigger>
          <TabsTrigger value="actions" data-testid="mat-tab-actions"><ListChecks className="h-3.5 w-3.5 mr-1" /> Actions & Review</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-5"><Overview data={overview} onOpenAssessment={openAssessment} /></TabsContent>
        <TabsContent value="organisation" className="mt-5"><OrganisationIssues onChanged={loadOverview} /></TabsContent>
        <TabsContent value="assessment" className="mt-5"><AssessmentEngagement onChanged={loadOverview} /></TabsContent>
        <TabsContent value="performance" className="mt-5"><IssuePerformance /></TabsContent>
        <TabsContent value="standards" className="mt-5"><StandardsDisclosures /></TabsContent>
        <TabsContent value="actions" className="mt-5"><ActionsReview /></TabsContent>
      </Tabs>
    </div>
  );
}
